import { CHATBOT_USER_ID } from "@redotech/ai-autopilot-constants/constant";
import { OverflowDirection } from "@redotech/react-util/overflow";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { getUserAvatarUrl } from "@redotech/redo-merchant-app-common/get-avatar-url";
import { MinimalConversation } from "@redotech/redo-model/conversation";
import { SupportPermissions } from "@redotech/redo-model/permissions";
import { GetUser as User } from "@redotech/redo-model/user";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { Flex } from "@redotech/redo-web/flex";
import ChatBotIcon from "@redotech/redo-web/icon-old/chat-bot.svg";
import { Text } from "@redotech/redo-web/text";
import { OverflowTooltip } from "@redotech/redo-web/tooltip/overflow-tooltip";
import { memo, useCallback, useState } from "react";
import * as assigneeDetailsCss from "./assignee-details.module.css";
import { AssigneePickerDropdown } from "./assignee-picker-dropdown";

export const AssigneeDetails = memo(function AssigneeDetails({
  updateConversationDetails,
  conversation,
}: {
  updateConversationDetails: ({ assignee }: { assignee: User | null }) => void;
  conversation: MinimalConversation;
}) {
  const canEditAssignee = useHasPermission(SupportPermissions.EDIT_ASSIGNEE);

  const isChatbot = conversation.assignee?._id === CHATBOT_USER_ID;

  const handleAssigneeChange = useCallback(
    (assignee: User | null) => {
      updateConversationDetails({ assignee });
    },
    [updateConversationDetails],
  );

  const [overflowRef, setOverflowRef] = useState<HTMLElement | null>(null);

  return (
    <div className={assigneeDetailsCss.assigneeDetails}>
      <Flex dir="column" gap="xs">
        <Flex align="center" justify="space-between">
          <Text fontSize="xs" fontWeight="medium" textColor="tertiary">
            Assignee
          </Text>
        </Flex>
        {canEditAssignee ? (
          <AssigneePickerDropdown
            assignee={conversation.assignee}
            onAssigneeChange={handleAssigneeChange}
          />
        ) : (
          <OverflowTooltip
            direction={OverflowDirection.Horizontal}
            overflowRef={overflowRef}
            tooltipProps={{
              placement: "top",
              title: `${conversation.assignee?.name || "None"}`,
            }}
          >
            <div className={assigneeDetailsCss.assigneeBadge}>
              <RedoBadge
                avatar={
                  !isChatbot
                    ? {
                        alt: "Assignee profile picture",
                        imageUrl: getUserAvatarUrl({
                          email: conversation.assignee?.email || "",
                          userId: conversation.assignee?._id,
                        }),
                        name: conversation.assignee?.name,
                      }
                    : undefined
                }
                color="gray"
                segmentLeading={
                  isChatbot ? { type: "icon", Icon: ChatBotIcon } : undefined
                }
                setRef={setOverflowRef}
                size="sm"
                text={conversation.assignee?.name || "None"}
              />
            </div>
          </OverflowTooltip>
        )}
      </Flex>
    </div>
  );
});
